// Branded HTML templates for OH emails. Each builder takes the OH doc plus a
// `sender` ({ firstname, lastname, email, phonenumber?, profilepicture? }) and
// returns a full HTML string ready to drop into a multipart/alternative MIME.
//
// Inline styles only — Gmail strips <style> blocks.

import { formatTimeRange } from "./openHouseHelpers";
import { feedbackUrlFor, DEFAULT_TEMPLATES, dataFor, lockboxSectionValue, fmtDate, sellerLine } from "./openHouseEmails";
import { forHostMessage } from "./openHouseHostAccess";
import { interpolate } from "./emailPlaceholders";
import { accessSection } from "./sellerDoorCode";

// Team Edelstein logo, white-on-transparent for use on the navy banner.
// Lives in Firebase Storage under email/ with a tokenized public download URL.
// 325x108 (3x the 36px display height) — the original secondary-logo-2-white.png
// is 9000px wide / 266KB, which made the logo load slowly in recipients' inboxes.
// The local copy at public/assets/email/te-logo-white-108.png is for the
// in-browser preview / dev only — outbound emails reference the Storage URL.
export const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/team-edelstein.firebasestorage.app/o/email%2Fte-logo-white-108.png?alt=media&token=88ead2af-0955-4fb4-b288-921aa56ff92b";

export const NAVY = "#1f2937";
export const NAVY_TINT = "#dbeafe";
export const TEXT_PRIMARY = "#111827";
export const TEXT_MUTED = "#6b7280";
export const TEXT_LIGHT = "#9ca3af";
export const BORDER = "#e5e7eb";
export const SURFACE = "#f9fafb";
export const FRAME_BG = "#f3f4f6";

// Helvetica-first for the body — cleaner and more "real estate brand" feel
// than the system stack. Arial falls back on Windows/Linux (renders nearly
// identical at email sizes).
const FONT = `Helvetica, Arial, sans-serif`;
// Serif for the property address — Georgia is the safest cross-client serif
// (bundled with both macOS and Windows; Gmail honors it). Times falls back
// for the rare client that doesn't have Georgia.
const SERIF = `Georgia, "Times New Roman", Times, serif`;

export function htmlEscape(s) {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Render plain-text body to HTML: each blank-line-separated chunk becomes a <p>.
// HTML-escapes each chunk so user-entered text can't inject markup.
export function paragraphsToHtml(text) {
  if (!text) return "";
  return String(text)
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p style="margin:0 0 14px;line-height:1.55;">${htmlEscape(p).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

// Banner. Logo on the left and the property address (white, bold, Helvetica)
// on the right — same row, vertically centered. Date+time sits below the
// address as a smaller, dimmed sub-line. The `tag` arg is kept for back-compat
// but no longer rendered (the subject line carries that info instead).
export function buildBanner(oh, tag) {
  let propertyAddress = "";
  let dateLine = "";
  if (typeof oh === "object" && oh) {
    propertyAddress = oh.propertyAddress || "";
    if (oh.date || oh.startTime || oh.endTime) {
      const datePart = oh.date ? fmtDate(oh.date) : "";
      const timePart = (oh.startTime && oh.endTime)
        ? formatTimeRange(oh.startTime, oh.endTime)
        : "";
      dateLine = [datePart, timePart].filter(Boolean).join(" · ");
    }
  }

  const addressBlock = propertyAddress
    ? `<div style="font-family:${FONT};color:#ffffff;font-size:18px;font-weight:700;line-height:1.3;letter-spacing:-0.005em;">${htmlEscape(propertyAddress)}</div>${dateLine ? `<div style="font-family:${FONT};color:#ffffff;opacity:0.65;font-size:12px;margin-top:4px;">${htmlEscape(dateLine)}</div>` : ""}`
    : "";

  return `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${NAVY};">
      <tr>
        <td style="padding:24px 32px;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
            <tr>
              <td align="left" style="vertical-align:middle;">
                <img src="${LOGO_URL}" alt="Team Edelstein Real Estate" height="36" style="display:block;height:36px;width:auto;border:0;">
              </td>
              <td align="right" style="vertical-align:middle;">
                ${addressBlock}
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `.trim();
}

function initialsOf(sender) {
  const f = (sender.firstname || sender.email || "").trim();
  const l = (sender.lastname || "").trim();
  return ((f[0] || "") + (l[0] || "")).toUpperCase() || "?";
}

function displayName(sender) {
  const parts = [sender.firstname, sender.lastname].filter(Boolean).join(" ").trim();
  if (parts) return parts;
  return String(sender.email || "").split("@")[0];
}

export function buildSignature(sender) {
  const avatarCell = sender.profilepicture
    ? `<img src="${htmlEscape(sender.profilepicture)}" alt="" width="52" height="52" style="display:block;width:52px;height:52px;border-radius:50%;border:0;object-fit:cover;">`
    : `<div style="width:52px;height:52px;border-radius:50%;background:${NAVY_TINT};color:${NAVY};text-align:center;line-height:52px;font-family:${FONT};font-weight:700;font-size:16px;letter-spacing:0.04em;">${htmlEscape(initialsOf(sender))}</div>`;

  // Skip phone if it's empty, looks like an email, or matches the sender's
  // email exactly. Some user docs mistakenly carry the email in `phonenumber`.
  const phoneRaw = String(sender.phonenumber || "").trim();
  const showPhone =
    phoneRaw &&
    !phoneRaw.includes("@") &&
    phoneRaw.toLowerCase() !== String(sender.email || "").toLowerCase();
  const contact = showPhone
    ? `${htmlEscape(phoneRaw)} &nbsp;&middot;&nbsp; ${htmlEscape(sender.email)}`
    : htmlEscape(sender.email);

  return `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-top:1px solid ${BORDER};">
      <tr>
        <td style="padding:22px 28px;">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0">
            <tr>
              <td style="padding-right:14px;vertical-align:middle;">${avatarCell}</td>
              <td style="vertical-align:middle;font-family:${FONT};font-size:12px;line-height:1.55;">
                <div style="font-family:${SERIF};color:${TEXT_PRIMARY};font-weight:500;font-size:18px;letter-spacing:-0.005em;">${htmlEscape(displayName(sender))}</div>
                <div style="color:${NAVY};font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:0.1em;margin:2px 0 4px;">Team Edelstein Real Estate</div>
                <div style="color:${TEXT_MUTED};">${contact}</div>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `.trim();
}

export function buildFooter(context) {
  return `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${SURFACE};border-top:1px solid ${BORDER};">
      <tr>
        <td align="center" style="padding:14px 28px;font-family:${FONT};font-size:11px;color:${TEXT_LIGHT};line-height:1.5;letter-spacing:0.01em;">
          ${htmlEscape(context)}
        </td>
      </tr>
    </table>
  `.trim();
}

export function buildSection(label, content) {
  return `
    <div style="margin:14px 0;">
      <div style="font-family:${FONT};font-size:11px;color:${TEXT_MUTED};text-transform:uppercase;letter-spacing:0.08em;font-weight:700;margin-bottom:4px;">${htmlEscape(label)}</div>
      <div style="font-family:${FONT};font-size:14px;color:${TEXT_PRIMARY};">${htmlEscape(content)}</div>
    </div>
  `.trim();
}

export function buildStatsTiles({ groups, secondShowings }) {
  const tile = (num, label) => `
    <td width="50%" style="padding:0 6px;">
      <div style="background:${SURFACE};border-left:3px solid ${NAVY};border-radius:4px;padding:16px 18px;text-align:left;">
        <div style="font-family:${SERIF};font-size:32px;font-weight:500;color:${NAVY};line-height:1;letter-spacing:-0.01em;">${htmlEscape(num)}</div>
        <div style="font-family:${FONT};font-size:10px;color:${TEXT_MUTED};text-transform:uppercase;letter-spacing:0.1em;margin-top:6px;font-weight:700;">${htmlEscape(label)}</div>
      </div>
    </td>
  `;
  return `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:18px 0 22px;">
      <tr>
        ${tile(groups, "Groups")}
        ${tile(secondShowings, "2nd Showings")}
      </tr>
    </table>
  `.trim();
}

export function buildButton(href, label) {
  return `
    <a href="${htmlEscape(href)}" style="display:inline-block;background:${NAVY};color:#ffffff;text-decoration:none;padding:10px 18px;border-radius:6px;font-family:${FONT};font-weight:600;font-size:13px;">${htmlEscape(label)}</a>
  `.trim();
}

export function wrapEmail({ banner, body, signature, footer }) {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
</head>
<body style="margin:0;padding:0;background:${FRAME_BG};">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${FRAME_BG};">
    <tr>
      <td align="center" style="padding:32px 20px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;background:#ffffff;border-radius:10px;overflow:hidden;box-shadow:0 1px 3px rgba(17,24,39,0.06),0 4px 12px rgba(17,24,39,0.05);">
          <tr><td>${banner}</td></tr>
          <tr><td style="padding:28px 32px 24px;font-family:${FONT};font-size:15px;line-height:1.55;color:${TEXT_PRIMARY};">${body}</td></tr>
          <tr><td>${signature}</td></tr>
          <tr><td>${footer}</td></tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// Schedule emails
// ---------------------------------------------------------------------------

// Recap feedback may carry the date as either a friendly string ("Sat, May 9")
// or a raw ISO date ("2026-05-09") that the public form's <input type="date">
// produced. Reformat if it looks ISO; otherwise pass through.
function smartFmtFeedbackDate(maybeDate, fallbackIso) {
  if (!maybeDate) return fmtDate(fallbackIso);
  const trimmed = String(maybeDate).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return fmtDate(trimmed);
  return trimmed;
}

// Recap feedback time may be a friendly string ("1:00pm – 3:00pm") or raw
// 24-hour HH:MM – HH:MM that the public form prepopulated from URL params.
// Reformat 24-hour pairs to 12-hour am/pm; otherwise pass through.
function smartFmtFeedbackTime(maybeTime, fallbackStart, fallbackEnd) {
  if (!maybeTime) return formatTimeRange(fallbackStart, fallbackEnd);
  const trimmed = String(maybeTime).trim();
  const m = trimmed.match(/^(\d{1,2}):(\d{2})\s*[–-]\s*(\d{1,2}):(\d{2})$/);
  if (m) return formatTimeRange(`${m[1]}:${m[2]}`, `${m[3]}:${m[4]}`);
  return trimmed;
}

export function buildUpdateAgentEmailHtml(oh, oldFields, newFields, sender, origin, overrides = {}) {
  // Decision 10: an outside host never sees the lockbox code or number (openHouseHostAccess.js).
  oh = forHostMessage(oh);
  const d = DEFAULT_TEMPLATES["update-agent"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  const oldStr = `${fmtDate(oldFields.date)}, ${formatTimeRange(oldFields.startTime, oldFields.endTime)}`;
  const newStr = `${fmtDate(newFields.date)}, ${formatTimeRange(newFields.startTime, newFields.endTime)}`;
  const url = feedbackUrlFor(oh, origin);
  const body = `
    ${paragraphsToHtml(interpolate(bodyTpl, data))}
    ${buildSection("Previously", oldStr)}
    ${buildSection("Now", newStr)}
    <div style="margin:18px 0 4px;">
      ${buildButton(url, "Submit Feedback")}
    </div>
  `;
  return wrapEmail({
    banner: buildBanner(oh),
    body,
    signature: buildSignature(sender),
    footer: buildFooter("You're receiving this because you're scheduled to host an open house with Team Edelstein."),
  });
}

export function buildSchedulingSellerEmailHtml(oh, sender, overrides = {}) {
  const d = DEFAULT_TEMPLATES["scheduling-seller"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  const body = paragraphsToHtml(interpolate(bodyTpl, data));
  return wrapEmail({
    banner: buildBanner(oh),
    body,
    signature: buildSignature(sender),
    footer: buildFooter("You're receiving this because we're working on the sale of your home."),
  });
}

export function buildUpdateSellerEmailHtml(oh, oldFields, newFields, sender, overrides = {}) {
  const d = DEFAULT_TEMPLATES["update-seller"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  const oldStr = `${fmtDate(oldFields.date)}, ${formatTimeRange(oldFields.startTime, oldFields.endTime)}`;
  const newStr = `${fmtDate(newFields.date)}, ${formatTimeRange(newFields.startTime, newFields.endTime)}`;
  const body = `
    ${paragraphsToHtml(interpolate(bodyTpl, data))}
    ${buildSection("Previously", oldStr)}
    ${buildSection("Now", newStr)}
  `;
  return wrapEmail({
    banner: buildBanner(oh),
    body,
    signature: buildSignature(sender),
    footer: buildFooter("You're receiving this because we're working on the sale of your home."),
  });
}

export function buildCancellationSellerEmailHtml(oh, reason, sender, overrides = {}) {
  const d = DEFAULT_TEMPLATES["cancellation-seller"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  const reasonBlock = (reason && reason.trim()) ? buildSection("Reason", reason.trim()) : "";
  const body = `
    ${paragraphsToHtml(interpolate(bodyTpl, data))}
    ${reasonBlock}
  `;
  return wrapEmail({
    banner: buildBanner(oh),
    body,
    signature: buildSignature(sender),
    footer: buildFooter("You're receiving this because we're working on the sale of your home."),
  });
}

export function buildCancellationAgentEmailHtml(oh, reason, sender, overrides = {}) {
  // Decision 10: an outside host never sees the lockbox code or number (openHouseHostAccess.js).
  oh = forHostMessage(oh);
  const d = DEFAULT_TEMPLATES["cancellation-agent"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  const reasonBlock = (reason && reason.trim()) ? buildSection("Reason", reason.trim()) : "";
  const body = `
    ${paragraphsToHtml(interpolate(bodyTpl, data))}
    ${reasonBlock}
  `;
  return wrapEmail({
    banner: buildBanner(oh),
    body,
    signature: buildSignature(sender),
    footer: buildFooter("You're receiving this because you're scheduled to host an open house with Team Edelstein."),
  });
}

export function buildRecapSellerEmailHtml(oh, fb, sender, overrides = {}) {
  const d = DEFAULT_TEMPLATES["recap-seller"];
  const introTpl = overrides.intro ?? d.intro;
  const closingTpl = overrides.closing ?? d.closing;
  const data = dataFor(oh, fb, `${introTpl}\n${closingTpl}`);
  data.date = smartFmtFeedbackDate(fb.date, oh.date);
  data.time = smartFmtFeedbackTime(fb.time, oh.startTime, oh.endTime);

  const intro = paragraphsToHtml(interpolate(introTpl, data));
  const closing = paragraphsToHtml(interpolate(closingTpl, data));
  const otherBlock = (fb.otherComments && fb.otherComments.trim())
    ? buildSection("Other comments", fb.otherComments.trim())
    : "";
  const body = `
    ${intro}
    ${buildStatsTiles({ groups: fb.groups, secondShowings: fb.secondShowings })}
    ${buildSection("Hosted by", fb.agentName || "")}
    ${buildSection("What showing agents said", fb.agentFeedback || "")}
    ${buildSection("What buyers said", fb.buyerFeedback || "")}
    ${otherBlock}
    ${closing}
  `;
  return wrapEmail({
    banner: buildBanner(oh),
    body,
    signature: buildSignature(sender),
    footer: buildFooter("You're receiving this because we're working on the sale of your home."),
  });
}

export function buildSchedulingAgentEmailHtml(oh, sender, origin, overrides = {}) {
  // Decision 10: an outside host never sees the lockbox code or number (openHouseHostAccess.js).
  oh = forHostMessage(oh);
  const d = DEFAULT_TEMPLATES["scheduling-agent"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  const url = feedbackUrlFor(oh, origin);
  // Every owner of the home, not just the one the record snapshotted (sellerLine, 09/25/2026).
  const sellers = sellerLine(oh);
  const sellerBlock = sellers ? buildSection("Seller", sellers) : "";
  // Seller Provided Door Code (Henry, 2026-09-18): a "Door Code" section when the listing has one,
  // otherwise today's "Lockbox" section, which lists EVERY box on the listing (Henry, 2026-09-25) in the
  // same words as the plain-text email, since both read openHouseEmails.lockboxSectionValue.
  const access = accessSection(lockboxSectionValue(oh), oh);
  const accessBlock = access ? buildSection(access.label, access.value) : "";
  const notesBlock = (oh.notes && oh.notes.trim()) ? buildSection("Notes from the listing agent", oh.notes.trim()) : "";
  const body = `
    ${paragraphsToHtml(interpolate(bodyTpl, data))}
    ${sellerBlock}
    ${accessBlock}
    ${notesBlock}
    <div style="margin:18px 0 4px;">
      ${buildButton(url, "Submit Feedback")}
      <p style="margin:6px 0 0;color:${TEXT_MUTED};font-size:12px;">No login required. Takes about 2 minutes.</p>
    </div>
  `;
  return wrapEmail({
    banner: buildBanner(oh),
    body,
    signature: buildSignature(sender),
    footer: buildFooter("You're receiving this because you're scheduled to host an open house with Team Edelstein."),
  });
}
